import flatMapDeep from "lodash.flatmapdeep";
import { writable } from "svelte/store";

export type ThemeName = "lightGreen" | "terminal" | "light";

export type Theme = {
  name: ThemeName;
  primary: {
    100: string;
    60: string;
    80: string;
    20: string;
    5: string;
  };
  secondary: {
    100: string;
    80: string;
    20: string;
  };
};

const lightGreenTheme: Theme = {
  name: "lightGreen",
  primary: {
    100: "#DDFFDC",
    80: "#DDFFDCCC",
    60: "#DDFFDC99",
    20: "#DDFFDC33",
    5: "#DDFFDC0D",
  },
  secondary: {
    100: "#2C332C",
    80: "#2C332CCC",
    20: "#2C332C33",
  },
};

const terminalTheme: Theme = {
  name: "terminal",
  primary: {
    100: "#7FEE64",
    80: "#7FEE64CC",
    60: "#7FEE6499",
    20: "#7FEE6433",
    5: "#7FEE640D",
  },
  secondary: {
    100: "#0D180A",
    80: "#0D180ACC",
    20: "#0D180A33",
  },
};

const lightTheme: Theme = {
  name: "light",
  primary: {
    100: "#212525",
    80: "#212525CC",
    60: "#21252599",
    20: "#21252533",
    5: "#2125250D",
  },
  secondary: {
    100: "#E9E9E9",
    80: "#E9E9E9CC",
    20: "#E9E9E933",
  },
};

export const themes = {
  lightGreen: lightGreenTheme,
  terminal: terminalTheme,
  light: lightTheme,
};

const isLocalStorageAvailable =
  typeof window !== "undefined" && window.localStorage;

const storedThemeName = isLocalStorageAvailable
  ? (localStorage.getItem("themeName") as ThemeName | null)
  : null;

const initialTheme =
  storedThemeName && themes[storedThemeName]
    ? themes[storedThemeName]
    : terminalTheme;

export const themeStore = writable(initialTheme);

export const isGlossaryMobileMenuOpen = writable(false);

export const collapsedStore = writable<{ [key: string]: boolean }>({});

export function toggleCollapsedState(href: string) {
  collapsedStore.update((store) => {
    return { ...store, [href]: !store[href] };
  });
}

type PageInfo = {
  title: string;
  href: string;
  abbreviation?: string;
};

export type Page = PageInfo & {
  pages?: Page[];
};

const flattenPages = (data: Page[]): Page[] => {
  return flatMapDeep(data, (page) => {
    return page.pages ? [page, ...flattenPages(page.pages)] : [page];
  });
};

export const findNextPage = (options: {
  currentHref: string;
  pages: Page[];
}): PageInfo | null => {
  const { currentHref, pages } = options;
  const flatPages = flattenPages(pages);

  const currentPageIndex = flatPages.findIndex(
    (page) => page.href === currentHref,
  );

  // Confirm the currentPage was found and that it's not the last page
  if (currentPageIndex !== -1 && currentPageIndex < flatPages.length - 1) {
    return flatPages[currentPageIndex + 1];
  }
  return null;
};

export const findPrevPage = (options: {
  currentHref: string;
  pages: Page[];
}): PageInfo | null => {
  const { currentHref, pages } = options;
  const flatPages = flattenPages(pages);

  const currentPageIndex = flatPages.findIndex(
    (page) => page.href === currentHref,
  );

  // Confirm the currentPage was found and that it's not the first page
  if (currentPageIndex !== -1 && currentPageIndex > 0) {
    return flatPages[currentPageIndex - 1];
  }
  return null;
};

export const findNearestParent = (options: {
  currentHref: string;
  pages: Page[];
}): PageInfo | null => {
  const { currentHref, pages } = options;

  function findParent(pages: Page[]): PageInfo | null {
    for (const page of pages) {
      if (page.pages) {
        const foundPage = page.pages.find((p) => p.href === currentHref);
        if (foundPage) {
          return page;
        } else {
          const parent = findParent(page.pages);
          if (parent) {
            return parent;
          }
        }
      }
    }
    return null;
  }
  return findParent(pages);
};

export const findChildren = (options: {
  currentHref: string;
  pages: Page[];
}): Page[] | null => {
  const { currentHref, pages } = options;

  function findChildren(pages: Page[]): Page[] | null {
    for (const page of pages) {
      if (page.href === currentHref && page.pages) {
        return page.pages;
      }
      if (page.pages) {
        const child = findChildren(page.pages);
        if (child) {
          return child;
        }
      }
    }
    return null;
  }

  return findChildren(pages);
};

// The menu closing happens too fast without the delay
export const closeMobileMenu = () => {
  setTimeout(() => {
    isGlossaryMobileMenuOpen.set(false);
  }, 200);
};
